import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import * as Lucide from "lucide-react";
import { Calendar as CalIcon, Users, ArrowRight, CheckCircle2, MessageCircle, Plus, Tag, X as XIcon } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useLang, tField } from "../lib/LanguageContext";
import { useCurrency } from "../lib/CurrencyContext";
import { api, imageUrl, whatsappLink } from "../lib/api";
import {
    computePrice,
    formatISO,
    nightsBetween,
    unavailableDateSet,
    computeAddonTotal,
} from "../lib/pricing";
import SEO from "../components/SEO";

export default function BookingForm() {
    const { t, lang } = useLang();
    const { format, currency } = useCurrency();
    const { site } = useOutletContext() || {};
    const [params] = useSearchParams();
    const preSlug = params.get("room");

    const [rooms, setRooms] = useState([]);
    const [addons, setAddons] = useState([]);
    const [selectedAddons, setSelectedAddons] = useState({}); // {addon_id: true/false}
    const [selectedSlug, setSelectedSlug] = useState(preSlug || "");
    const [range, setRange] = useState({ from: undefined, to: undefined });
    const [guests, setGuests] = useState(2);
    const [unavailable, setUnavailable] = useState(new Set());

    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState(null);

    // Promo code state
    const [promoInput, setPromoInput] = useState("");
    const [promoApplied, setPromoApplied] = useState(null); // {code, discount, discount_type, discount_value}
    const [promoError, setPromoError] = useState("");
    const [promoBusy, setPromoBusy] = useState(false);

    useEffect(() => {
        api.get("/rooms").then((r) => setRooms(r.data));
        api.get("/addons").then((r) => setAddons(r.data)).catch(() => {});
    }, []);

    // Set default room when rooms load (from URL param or first room)
    useEffect(() => {
        if (rooms.length === 0) return;
        if (selectedSlug && rooms.find((r) => r.slug === selectedSlug)) return;
        setSelectedSlug(preSlug && rooms.find((r) => r.slug === preSlug) ? preSlug : rooms[0].slug);
    }, [rooms]);

    // Load unavailable dates when room changes
    useEffect(() => {
        if (!selectedSlug) return;
        api.get(`/availability?room_slug=${selectedSlug}`).then((r) => {
            setUnavailable(unavailableDateSet(r.data.ranges));
        });
    }, [selectedSlug]);

    const selectedRoom = useMemo(
        () => rooms.find((r) => r.slug === selectedSlug),
        [rooms, selectedSlug],
    );

    const check_in = range.from ? formatISO(range.from) : "";
    const check_out = range.to ? formatISO(range.to) : "";
    const priceInfo = useMemo(
        () => computePrice(selectedRoom, check_in, check_out),
        [selectedRoom, check_in, check_out],
    );

    // Compute addon line items based on current nights/guests
    const addonLines = useMemo(() => {
        return addons
            .filter((a) => selectedAddons[a.id])
            .map((a) => ({
                id: a.id,
                title: tField(a, "title", lang),
                price_type: a.price_type,
                unit_price: a.price,
                qty: a.price_type === "per_night" ? priceInfo.nights
                    : a.price_type === "per_person" ? guests
                    : a.price_type === "per_person_night" ? priceInfo.nights * guests
                    : 1,
                total: computeAddonTotal(a, priceInfo.nights, guests),
            }));
    }, [addons, selectedAddons, priceInfo.nights, guests, lang]);

    const addonsTotal = addonLines.reduce((s, x) => s + x.total, 0);

    // Recompute promo discount based on currently-applied promo + new room subtotal
    const promoDiscount = useMemo(() => {
        if (!promoApplied) return 0;
        const subtotal = priceInfo.total; // promo applies on room subtotal only
        const v = Number(promoApplied.discount_value) || 0;
        if (promoApplied.discount_type === "percentage") {
            return Math.round(subtotal * v) / 100;
        }
        return Math.min(v, subtotal);
    }, [promoApplied, priceInfo.total]);

    const grandTotal = Math.max(0, priceInfo.total + addonsTotal - promoDiscount);

    const whatsapp = site?.whatsapp || "355682116731";

    // Re-validate promo when room or nights change (in case room not eligible or min_nights not met)
    useEffect(() => {
        if (!promoApplied || !selectedRoom) return;
        api.post("/promo/validate", {
            code: promoApplied.code,
            subtotal: priceInfo.total,
            room_slug: selectedRoom.slug || "",
            nights: priceInfo.nights,
        }).then((r) => {
            if (!r.data.valid) {
                setPromoApplied(null);
                setPromoError(r.data.error || "invalid");
            }
        }).catch(() => {});
    }, [selectedRoom?.slug, priceInfo.nights]);

    const applyPromo = async () => {
        const code = (promoInput || "").trim();
        if (!code) return;
        setPromoBusy(true);
        setPromoError("");
        try {
            const res = await api.post("/promo/validate", {
                code,
                subtotal: priceInfo.total,
                room_slug: selectedRoom?.slug || "",
                nights: priceInfo.nights,
            });
            if (res.data.valid) {
                setPromoApplied({
                    code: res.data.code,
                    discount: res.data.discount,
                    discount_type: res.data.discount_type,
                    discount_value: res.data.discount_value,
                });
                setPromoError("");
                toast.success(
                    lang === "al"
                        ? `Kodi u aplikua! Kursyet ${format(res.data.discount)}`
                        : lang === "it"
                          ? `Codice applicato! Risparmi ${format(res.data.discount)}`
                          : lang === "de"
                            ? `Code angewendet! Sie sparen ${format(res.data.discount)}`
                            : `Code applied! You saved ${format(res.data.discount)}`
                );
            } else {
                setPromoApplied(null);
                setPromoError(res.data.error || "invalid");
            }
        } catch {
            setPromoError("network");
        } finally {
            setPromoBusy(false);
        }
    };

    const removePromo = () => {
        setPromoApplied(null);
        setPromoError("");
        setPromoInput("");
    };

    const promoErrorText = (() => {
        const errors = {
            al: {
                not_found: "Ky kod nuk ekziston.",
                inactive: "Ky kod nuk është aktiv.",
                not_started: "Ky kod nuk ka filluar ende.",
                expired: "Ky kod ka skaduar.",
                max_uses: "Ky kod ka arritur limitin maksimal.",
                room_not_allowed: "Ky kod nuk vlen për këtë dhomë.",
                min_nights: "Ky kod kërkon më shumë netë.",
                network: "Ndodhi një gabim. Provoni përsëri.",
                empty: "Vendos një kod.",
                invalid: "Kod i pavlefshëm.",
            },
            en: {
                not_found: "This code doesn't exist.",
                inactive: "This code is not active.",
                not_started: "This code hasn't started yet.",
                expired: "This code has expired.",
                max_uses: "This code has reached its maximum uses.",
                room_not_allowed: "This code is not valid for this room.",
                min_nights: "This code requires more nights.",
                network: "Something went wrong. Try again.",
                empty: "Enter a code.",
                invalid: "Invalid code.",
            },
            it: {
                not_found: "Codice non esistente.",
                inactive: "Codice non attivo.",
                not_started: "Codice non ancora valido.",
                expired: "Codice scaduto.",
                max_uses: "Codice esaurito.",
                room_not_allowed: "Codice non valido per questa camera.",
                min_nights: "Codice richiede più notti.",
                network: "Errore. Riprova.",
                empty: "Inserisci un codice.",
                invalid: "Codice non valido.",
            },
            de: {
                not_found: "Code existiert nicht.",
                inactive: "Code ist nicht aktiv.",
                not_started: "Code ist noch nicht gültig.",
                expired: "Code abgelaufen.",
                max_uses: "Code hat das Limit erreicht.",
                room_not_allowed: "Code gilt nicht für dieses Zimmer.",
                min_nights: "Code erfordert mehr Nächte.",
                network: "Fehler. Erneut versuchen.",
                empty: "Code eingeben.",
                invalid: "Ungültiger Code.",
            },
        };
        return errors[lang]?.[promoError] || errors.en[promoError] || "";
    })();

    const formatPriceType = (type) => {
        if (lang === "al") {
            if (type === "per_night") return "/ natë";
            if (type === "per_person") return "/ person";
            if (type === "per_person_night") return "/ person / natë";
            return "fiks";
        }
        if (lang === "it") {
            if (type === "per_night") return "/ notte";
            if (type === "per_person") return "/ persona";
            if (type === "per_person_night") return "/ pers. / notte";
            return "fisso";
        }
        if (lang === "de") {
            if (type === "per_night") return "/ Nacht";
            if (type === "per_person") return "/ Pers.";
            if (type === "per_person_night") return "/ Pers. / Nacht";
            return "Pauschal";
        }
        if (type === "per_night") return "/ night";
        if (type === "per_person") return "/ person";
        if (type === "per_person_night") return "/ pers. / night";
        return "one-time";
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!selectedRoom) {
            toast.error(lang === "al" ? "Zgjidh një dhomë" : "Please select a room");
            return;
        }
        if (!range.from || !range.to) {
            toast.error(lang === "al" ? "Zgjidh datat" : "Please select dates");
            return;
        }
        if (nightsBetween(check_in, check_out) < 1) {
            toast.error(lang === "al" ? "Të paktën 1 natë" : "At least 1 night required");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                room_id: selectedRoom.id,
                room_slug: selectedRoom.slug,
                check_in,
                check_out,
                guests,
                total_price: grandTotal,
                nights: priceInfo.nights,
                addons: addonLines,
                addons_total: addonsTotal,
                promo_code: promoApplied?.code || "",
                promo_discount: promoDiscount,
            };
            const res = await api.post("/bookings", payload);
            setSuccess(res.data);

            // Build WhatsApp message
            const roomTitle = tField(selectedRoom, "title", lang);
            const addonsText = addonLines.length
                ? `%0A• ${lang === "al" ? "Shërbime ekstra" : lang === "it" ? "Servizi extra" : lang === "de" ? "Extras" : "Extras"}:${addonLines.map((a) => `%0A   - ${a.title} × ${a.qty} = ${format(a.total)}`).join("")}`
                : "";
            const promoText = promoApplied
                ? `%0A• ${lang === "al" ? "Promo Code" : "Promo Code"}: ${promoApplied.code} (-${format(promoDiscount)})`
                : "";
            const msg =
                lang === "al"
                    ? `Përshëndetje! Dëshiroj të rezervoj te Kompleksi Dea:%0A• Dhoma: ${roomTitle}%0A• Hyrja: ${check_in}%0A• Dalja: ${check_out}%0A• Net: ${priceInfo.nights}%0A• Mysafirë: ${guests}${addonsText}${promoText}%0A• Çmimi total: ${format(grandTotal)} (${currency})%0A• Emri: ${form.name}%0A• Telefon: ${form.phone}%0A%0A${form.message || ""}`
                    : `Hello! I would like to book at Kompleksi Dea:%0A• Room: ${roomTitle}%0A• Check-in: ${check_in}%0A• Check-out: ${check_out}%0A• Nights: ${priceInfo.nights}%0A• Guests: ${guests}${addonsText}${promoText}%0A• Total price: ${format(grandTotal)} (${currency})%0A• Name: ${form.name}%0A• Phone: ${form.phone}%0A%0A${form.message || ""}`;

            window.open(whatsappLink(whatsapp, decodeURIComponent(msg)), "_blank");
        } catch {
            toast.error(lang === "al" ? "Pati një gabim" : "Something went wrong");
        } finally {
            setBusy(false);
        }
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isDateDisabled = (date) => {
        if (date < today) return true;
        return unavailable.has(formatISO(date));
    };

    if (success) {
        return (
            <div className="min-h-screen bg-[#F4EFE6] flex items-center pt-32 pb-20 px-6" data-testid="booking-success">
                <SEO title={lang === "al" ? "Rezervimi u dërgua" : "Booking sent"} path="/book" />
                <div className="max-w-xl mx-auto bg-white rounded-2xl p-10 text-center shadow-[0_20px_60px_rgb(0,0,0,0.08)]">
                    <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle2 size={32} className="text-green-600" />
                    </div>
                    <h1 className="font-serif text-3xl text-[#0F3A59] mt-6">
                        {lang === "al" ? "Faleminderit!" : "Thank you!"}
                    </h1>
                    <p className="text-[#1C1C1C]/70 font-light mt-3">
                        {lang === "al"
                            ? "Kërkesa juaj u regjistrua dhe ju kemi hapur WhatsApp për konfirmim të shpejtë. Do t'ju kontaktojmë sa më shpejt."
                            : "Your request has been registered. We've opened WhatsApp for fast confirmation. We'll contact you soon."}
                    </p>
                    <div className="mt-8 p-5 bg-[#F4EFE6] rounded-xl text-left text-sm space-y-1.5 font-light">
                        <div><strong>{lang === "al" ? "Dhoma" : "Room"}:</strong> {tField(selectedRoom, "title", lang)}</div>
                        <div><strong>{lang === "al" ? "Hyrja" : "Check-in"}:</strong> {check_in}</div>
                        <div><strong>{lang === "al" ? "Dalja" : "Check-out"}:</strong> {check_out}</div>
                        <div><strong>{lang === "al" ? "Net" : "Nights"}:</strong> {priceInfo.nights}</div>
                        <div><strong>{lang === "al" ? "Mysafirë" : "Guests"}:</strong> {guests}</div>
                        <div><strong>{lang === "al" ? "Total" : "Total"}:</strong> {format(grandTotal)}</div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
                        <a
                            href={whatsappLink(whatsapp, lang === "al" ? "Përshëndetje, kam dërguar një rezervim." : "Hello, I sent a booking request.")}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-[#25D366] hover:bg-[#128C7E] text-white px-6 py-3 rounded-full text-sm flex items-center gap-2 justify-center"
                        >
                            <MessageCircle size={16} />
                            WhatsApp
                        </a>
                        <Link
                            to="/"
                            className="border border-[#0F3A59] text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white px-6 py-3 rounded-full text-sm transition"
                        >
                            {lang === "al" ? "Kthehu te faqja" : "Back to home"}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#FDFBF7]" data-testid="booking-page">
            <SEO
                title={lang === "al" ? "Rezervo Tani" : "Book Now"}
                description={lang === "al" ? "Rezervo dhomën tënde në Kompleksin Dea Radhimë me data dhe çmim transparent." : "Book your room at Kompleksi Dea Radhimë with transparent dates and pricing."}
                path="/book"
            />

            {/* Header */}
            <section className="relative h-[40vh] min-h-[300px] flex items-end overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${imageUrl(site?.booking_hero_image, imageUrl("seed:gallery_pool"))})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/40" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-12 pt-32 w-full">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                        <span className="text-xs tracking-[0.3em] uppercase text-white/80">
                            {lang === "al" ? "Rezervo" : "Reservation"}
                        </span>
                        <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">
                            {lang === "al" ? "Rezervo qëndrimin tënd" : "Book your stay"}
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Booking form */}
            <section className="py-12 md:py-16 px-6 md:px-12 max-w-6xl mx-auto">
                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Form column */}
                    <form
                        onSubmit={submit}
                        className="lg:col-span-2 bg-white rounded-2xl p-6 md:p-10 border border-[#E5E0D8] shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-6"
                        data-testid="booking-form"
                    >
                        <h2 className="font-serif text-2xl md:text-3xl text-[#0F3A59]">
                            {lang === "al" ? "Detajet e rezervimit" : "Booking details"}
                        </h2>

                        {/* Room */}
                        <div>
                            <label className="text-xs uppercase tracking-wider text-[#1C1C1C]/70 mb-2 block">
                                {lang === "al" ? "Lloji i dhomës" : "Room type"}
                            </label>
                            <Select value={selectedSlug} onValueChange={setSelectedSlug}>
                                <SelectTrigger data-testid="booking-room-select" className="h-12 rounded-lg border-[#E5E0D8]">
                                    <SelectValue placeholder={lang === "al" ? "Zgjidh dhomën" : "Choose room"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {rooms.map((r) => (
                                        <SelectItem key={r.id} value={r.slug}>
                                            {tField(r, "title", lang)} — {format(r.price)}/{t("per_night").replace("/", "").trim()}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Dates */}
                        <div>
                            <label className="text-xs uppercase tracking-wider text-[#1C1C1C]/70 mb-2 block">
                                {lang === "al" ? "Datat" : "Dates"}
                            </label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        data-testid="booking-date-trigger"
                                        className="w-full h-12 rounded-lg border border-[#E5E0D8] px-4 flex items-center justify-between bg-white text-left text-sm hover:border-[#0F3A59] transition"
                                    >
                                        <span className="flex items-center gap-2">
                                            <CalIcon size={16} strokeWidth={1.5} className="text-[#0F3A59]" />
                                            {range.from && range.to
                                                ? `${formatISO(range.from)} → ${formatISO(range.to)}`
                                                : lang === "al"
                                                  ? "Zgjidh datat e qëndrimit"
                                                  : "Pick check-in & check-out"}
                                        </span>
                                        {range.from && range.to && (
                                            <span className="text-[#0F3A59] font-medium ml-3">
                                                {priceInfo.nights} {lang === "al" ? "net" : "nights"}
                                            </span>
                                        )}
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="range"
                                        selected={range}
                                        onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
                                        disabled={isDateDisabled}
                                        numberOfMonths={2}
                                        defaultMonth={range.from || today}
                                    />
                                </PopoverContent>
                            </Popover>
                            {unavailable.size > 0 && (
                                <p className="text-xs text-[#1C1C1C]/60 mt-2">
                                    {lang === "al"
                                        ? "Disa data nuk janë në dispozicion."
                                        : "Some dates are unavailable."}
                                </p>
                            )}
                        </div>

                        {/* Guests */}
                        <div>
                            <label className="text-xs uppercase tracking-wider text-[#1C1C1C]/70 mb-2 block">
                                {lang === "al" ? "Numri i mysafirëve" : "Number of guests"}
                            </label>
                            <Select value={String(guests)} onValueChange={(v) => setGuests(parseInt(v))}>
                                <SelectTrigger data-testid="booking-guests" className="h-12 rounded-lg border-[#E5E0D8]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[1, 2, 3, 4, 5, 6].map((n) => (
                                        <SelectItem key={n} value={String(n)}>
                                            <span className="flex items-center gap-2">
                                                <Users size={14} /> {n}{" "}
                                                {n === 1
                                                    ? lang === "al" ? "mysafir" : "guest"
                                                    : lang === "al" ? "mysafirë" : "guests"}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Add-ons / Shërbime ekstra */}
                        {addons.length > 0 && (
                            <div className="pt-4 border-t border-[#E5E0D8]">
                                <label className="text-xs uppercase tracking-wider text-[#1C1C1C]/70 mb-3 block">
                                    {lang === "al" ? "Shërbime ekstra" : lang === "it" ? "Servizi extra" : lang === "de" ? "Zusatzleistungen" : "Extra services"}
                                </label>
                                <div className="grid sm:grid-cols-2 gap-3" data-testid="addons-grid">
                                    {addons.map((a) => {
                                        const checked = !!selectedAddons[a.id];
                                        const Icon = Lucide[a.icon] || Plus;
                                        const lineTotal = computeAddonTotal(a, priceInfo.nights, guests);
                                        return (
                                            <button
                                                key={a.id}
                                                type="button"
                                                onClick={() => setSelectedAddons({ ...selectedAddons, [a.id]: !checked })}
                                                data-testid={`addon-${a.id}`}
                                                className={`text-left p-4 rounded-xl border transition-all ${
                                                    checked
                                                        ? "border-[#0F3A59] bg-[#0F3A59]/5 shadow-[0_4px_15px_rgba(15,58,89,0.08)]"
                                                        : "border-[#E5E0D8] hover:border-[#0F3A59]/40"
                                                }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                                                        checked ? "bg-[#0F3A59] text-white" : "bg-[#F4EFE6] text-[#0F3A59]"
                                                    }`}>
                                                        {checked ? <CheckCircle2 size={18} /> : <Icon size={16} strokeWidth={1.6} />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-sm text-[#0F3A59] leading-snug">
                                                            {tField(a, "title", lang)}
                                                        </div>
                                                        <div className="text-xs text-[#1C1C1C]/60 font-light mt-1 line-clamp-2">
                                                            {tField(a, "description", lang)}
                                                        </div>
                                                        <div className="mt-2 flex items-baseline gap-1">
                                                            <span className="font-serif text-base text-[#0F3A59]">{format(a.price)}</span>
                                                            <span className="text-[10px] text-[#1C1C1C]/55 uppercase tracking-wider">
                                                                {formatPriceType(a.price_type)}
                                                            </span>
                                                            {checked && lineTotal > 0 && (
                                                                <span className="ml-auto text-xs font-medium text-[#0F3A59]" data-testid={`addon-line-${a.id}`}>
                                                                    = {format(lineTotal)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Promo Code */}
                        <div className="pt-4 border-t border-[#E5E0D8]">
                            <label className="text-xs uppercase tracking-wider text-[#1C1C1C]/70 mb-3 block">
                                {lang === "al" ? "Kod Promocional" : lang === "it" ? "Codice Promo" : lang === "de" ? "Promo-Code" : "Promo Code"}
                            </label>
                            {promoApplied ? (
                                <div
                                    data-testid="promo-applied"
                                    className="flex items-center justify-between gap-3 p-4 rounded-xl border border-green-500/40 bg-green-50"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="shrink-0 w-9 h-9 rounded-full bg-green-500 text-white flex items-center justify-center">
                                            <CheckCircle2 size={18} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-semibold text-sm text-green-800 tracking-wide truncate">
                                                {promoApplied.code}
                                            </div>
                                            <div className="text-xs text-green-700 font-light">
                                                {lang === "al" ? "Kursyet" : lang === "it" ? "Risparmi" : lang === "de" ? "Sie sparen" : "You saved"}{" "}
                                                <span className="font-semibold">{format(promoDiscount)}</span>
                                                {promoApplied.discount_type === "percentage" && (
                                                    <span className="text-green-600/70 ml-1">
                                                        ({promoApplied.discount_value}%)
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={removePromo}
                                        data-testid="promo-remove"
                                        className="shrink-0 w-9 h-9 rounded-full text-green-700 hover:bg-green-100 flex items-center justify-center transition"
                                        aria-label="Remove promo"
                                    >
                                        <XIcon size={16} />
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Tag
                                                size={14}
                                                strokeWidth={1.5}
                                                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#0F3A59]/60 pointer-events-none"
                                            />
                                            <Input
                                                value={promoInput}
                                                onChange={(e) => {
                                                    setPromoInput(e.target.value.toUpperCase());
                                                    if (promoError) setPromoError("");
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        applyPromo();
                                                    }
                                                }}
                                                placeholder={lang === "al" ? "P.sh. SUMMER2026" : "e.g. SUMMER2026"}
                                                data-testid="promo-input"
                                                className={`h-12 pl-10 rounded-lg uppercase tracking-wider ${
                                                    promoError ? "border-red-400 focus-visible:ring-red-200" : "border-[#E5E0D8]"
                                                }`}
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            onClick={applyPromo}
                                            disabled={promoBusy || !promoInput.trim()}
                                            data-testid="promo-apply"
                                            className="h-12 px-6 rounded-lg bg-[#0F3A59] hover:bg-[#1C5279] text-white text-sm tracking-wide"
                                        >
                                            {promoBusy
                                                ? lang === "al" ? "..." : "..."
                                                : lang === "al" ? "Apliko" : lang === "it" ? "Applica" : lang === "de" ? "Anwenden" : "Apply"}
                                        </Button>
                                    </div>
                                    {promoError && (
                                        <p
                                            data-testid="promo-error"
                                            className="text-xs text-red-600 font-light mt-2 flex items-center gap-1.5"
                                        >
                                            <XIcon size={12} />
                                            {promoErrorText}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Contact */}
                        <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-[#E5E0D8]">
                            <Input
                                placeholder={lang === "al" ? "Emri juaj" : "Your name"}
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                                data-testid="booking-name"
                                className="h-12 rounded-lg border-[#E5E0D8]"
                            />
                            <Input
                                type="email"
                                placeholder="Email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                required
                                data-testid="booking-email"
                                className="h-12 rounded-lg border-[#E5E0D8]"
                            />
                            <Input
                                type="tel"
                                placeholder={lang === "al" ? "Telefoni" : "Phone"}
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                data-testid="booking-phone"
                                className="h-12 rounded-lg border-[#E5E0D8] sm:col-span-2"
                            />
                            <Textarea
                                placeholder={lang === "al" ? "Mesazh shtesë (opsionale)" : "Additional message (optional)"}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                rows={3}
                                data-testid="booking-message"
                                className="rounded-lg border-[#E5E0D8] sm:col-span-2 resize-none"
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={busy}
                            data-testid="booking-submit"
                            className="w-full bg-[#0F3A59] hover:bg-[#1C5279] text-white h-12 rounded-full font-medium tracking-wide text-base"
                        >
                            {busy
                                ? lang === "al" ? "Po dërgohet..." : "Sending..."
                                : lang === "al" ? "Dërgo dhe hape WhatsApp" : "Send & open WhatsApp"}
                            <ArrowRight size={16} className="ml-2" />
                        </Button>
                    </form>

                    {/* Summary column */}
                    <aside className="bg-[#F4EFE6] rounded-2xl p-6 md:p-8 h-fit sticky top-24" data-testid="booking-summary">
                        {selectedRoom && (
                            <>
                                <div className="aspect-video rounded-lg overflow-hidden mb-5">
                                    <img decoding="async" loading="lazy"
                                        src={imageUrl(selectedRoom.images?.[0], imageUrl("seed:room_standard"))}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <h3 className="font-serif text-xl text-[#0F3A59]">
                                    {tField(selectedRoom, "title", lang)}
                                </h3>
                                <p className="text-xs text-[#1C1C1C]/65 font-light mt-2 line-clamp-3">
                                    {tField(selectedRoom, "description", lang)}
                                </p>

                                <div className="mt-6 pt-6 border-t border-[#0F3A59]/10 space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-[#1C1C1C]/70">
                                            {lang === "al" ? "Net" : "Nights"}
                                        </span>
                                        <span className="font-medium" data-testid="summary-nights">{priceInfo.nights}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-[#1C1C1C]/70">
                                            {lang === "al" ? "Mysafirë" : "Guests"}
                                        </span>
                                        <span className="font-medium">{guests}</span>
                                    </div>
                                </div>
                                {priceInfo.breakdown.length > 0 && (
                                    <details className="mt-4 text-xs text-[#1C1C1C]/65 font-light">
                                        <summary className="cursor-pointer hover:text-[#0F3A59]">
                                            {lang === "al" ? "Shiko ndarjen për natë" : "View nightly breakdown"}
                                        </summary>
                                        <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5">
                                            {priceInfo.breakdown.map((b) => (
                                                <div key={b.date} className="flex justify-between">
                                                    <span>{b.date}</span>
                                                    <span>{format(b.price)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </details>
                                )}
                                {addonLines.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-[#0F3A59]/10 space-y-1.5" data-testid="addons-summary">
                                        <div className="text-xs uppercase tracking-wider text-[#1C1C1C]/70 mb-1">
                                            {lang === "al" ? "Shërbime ekstra" : lang === "it" ? "Servizi extra" : lang === "de" ? "Extras" : "Extras"}
                                        </div>
                                        {addonLines.map((a) => (
                                            <div key={a.id} className="flex justify-between text-xs font-light">
                                                <span className="text-[#1C1C1C]/70 truncate pr-2">
                                                    {a.title}{a.qty > 1 ? ` × ${a.qty}` : ""}
                                                </span>
                                                <span className="font-medium text-[#0F3A59] shrink-0">{format(a.total)}</span>
                                            </div>
                                        ))}
                                        <div className="flex justify-between text-xs pt-1.5 mt-1.5 border-t border-dashed border-[#0F3A59]/10">
                                            <span className="text-[#1C1C1C]/70">
                                                {lang === "al" ? "Nën-total dhoma" : "Room subtotal"}
                                            </span>
                                            <span className="font-medium">{format(priceInfo.total)}</span>
                                        </div>
                                    </div>
                                )}
                                <div className="mt-6 pt-6 border-t border-[#0F3A59]/10 flex items-baseline justify-between">
                                    <span className="text-sm uppercase tracking-wider text-[#1C1C1C]/70">
                                        Total
                                    </span>
                                    <span className="font-serif text-3xl text-[#0F3A59]" data-testid="summary-total">
                                        {format(grandTotal)}
                                    </span>
                                </div>
                                {promoApplied && promoDiscount > 0 && (
                                    <div data-testid="summary-promo" className="mt-3 flex items-center justify-between text-xs">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full font-medium tracking-wide">
                                            <Tag size={10} />
                                            {promoApplied.code}
                                        </span>
                                        <span className="text-green-700 font-medium">
                                            −{format(promoDiscount)}
                                        </span>
                                    </div>
                                )}
                                <p className="text-xs text-[#1C1C1C]/55 mt-3 font-light">
                                    {lang === "al"
                                        ? "Çmimi konfirmohet me e-mail/WhatsApp."
                                        : "Final price confirmed via email/WhatsApp."}
                                </p>
                            </>
                        )}
                    </aside>
                </div>
            </section>
        </div>
    );
}
